import { MMButton, ModalContentSurface, ModalFooter, ModalSubtitle, ModalTitle } from '@components'
import cn from '@integrations/classnames'
import toast from '@integrations/notifications'
import { useContext } from '@integrations/react'
import { useAccountEditQry } from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { GlobalContext, tr } from 'mmfintech-commons'
import { ErrorDisplay, Input } from 'mmfintech-portal-commons'
import styles from '../AccountCreateModal/AccountCreateModal.module.scss'

interface Props {
  account: AccountBalanceResponse
  onSuccess: () => void
}

function AccountUpdateModal({ account, onSuccess }: Props) {
  const { modalHide } = useContext(GlobalContext)

  const { id: accountId, name: accountName, currencyCode } = account || {}

  function handleSuccessfulChange() {
    toast.remove()
    toast.success(tr('FRONTEND.ACCOUNTS.UPDATE.SUCCESS', 'Account successfully updated'))
    onSuccess()
    modalHide()
  }

  const { formValues, editAccountError, editAccountFetching, handleSubmit } = useAccountEditQry({
    accountId,
    accountName,
    onSuccess: handleSuccessfulChange
  })

  return (
    <ModalContentSurface className={cn('account-create-wrapper')} data-test='create-account-modal'>
      <div className={cn(styles['account-create-header'], 'account-create-header')}>
        <ModalTitle className={cn(styles['account-create-title'], 'account-create-title')}>
          {tr('FRONTEND.ACCOUNTS.UPDATE.TITLE', 'Edit account')}
        </ModalTitle>
        <ModalSubtitle>
          {tr('FRONTEND.ACCOUNTS.UPDATE.SUBTITLE', '{{CURRENCY}} account · ID {{ID}}', {
            CURRENCY: currencyCode,
            ID: accountId
          })}
        </ModalSubtitle>
      </div>

      <form className={cn(styles['account-create-form'], 'account-create-form')} noValidate>
        <Input
          type='text'
          data-test='account-name'
          label={tr('FRONTEND.ACCOUNTS.CREATE.NAME_LABEL', 'Account name')}
          {...formValues.registerInput('name')}
          autoFocus
        />

        <ErrorDisplay error={editAccountError} />

        <ModalFooter>
          <MMButton
            type='button'
            variant='secondary'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={modalHide}
            data-test='button-cancel'
          />
          <MMButton
            type='button'
            variant='primary'
            caption={tr('FRONTEND.BUTTONS.SAVE', 'Save')}
            onClick={handleSubmit}
            loading={editAccountFetching}
            data-test='button-submit'
          />
        </ModalFooter>
      </form>
    </ModalContentSurface>
  )
}

export default AccountUpdateModal
